import * as React from 'react';
import { Paper, Popover, styled, Typography } from '@mui/material';

const InteractiveCaption = styled(Typography)(({ theme }) => ({
    cursor: 'help',
    position: 'relative',
    paddingBottom: theme.spacing(0.25),
    '&:before': {
        position: 'absolute',
        content: 'no-open-quote',
        display: 'block',
        bottom: theme.spacing(0.25),
        left: theme.spacing(0.25),
        right: theme.spacing(0.5),
        borderColor: 'inherit',
        borderBottom: '1px dashed'
    },
    '&:hover': {
        '&:before': {
            borderBottom: '1px solid'
        }
    }
}));

interface EngagementTextProps {
    text: string | React.ReactNode;
    tooltipTitle: string;
    tooltipText: string;
}

export const EngagementText: React.FC<EngagementTextProps> = ({ text, tooltipTitle, tooltipText }) => {
    const [anchor, setAnchor] = React.useState<HTMLDivElement | null>(null);
    const handleMouseEnter = React.useCallback((e: React.MouseEvent) => {
        setAnchor(e.currentTarget as HTMLDivElement);
    }, []);
    const handleMouseLeave = React.useCallback(() => {
        setAnchor(null);
    }, []);
    return (
        <>
            <InteractiveCaption
                variant="caption"
                aria-owns={!!anchor ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {text}
            </InteractiveCaption>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={handleMouseLeave}
                aaria-hidden="false"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                sx={{
                    pointerEvents: 'none'
                }}
                disableRestoreFocus
            >
                <Paper elevation={3} sx={{ p: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'bold',
                            opacity: 0.75
                        }}
                    >
                        {tooltipTitle}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6, mt: 0.25 }}>
                        {tooltipText}
                    </Typography>
                </Paper>
            </Popover>
        </>
    );
};
